/**
 * Global fade-in image loader
 * Handles adding 'loaded' class to .fade-in elements when images finish loading
 */
(function() {
  'use strict';

  /**
   * Adds loaded class when image finishes loading (or on error)
   * @param {HTMLElement} element - Element to add loaded class to
   * @param {HTMLImageElement} img - Image to track loading state
   */
  function handleImageLoad(element, img) {
    // Skip if already marked as loaded
    if (element.classList.contains('loaded')) return;

    function markAsLoaded() {
      element.classList.add('loaded');
    }

    // Handle both successful loads and errors
    img.addEventListener('load', markAsLoaded, { once: true });
    img.addEventListener('error', markAsLoaded, { once: true });

    // If already loaded (cached), add class immediately
    if (img.complete) {
      markAsLoaded();
    }
  }

  /**
   * Process a fade-in image element
   * @param {HTMLImageElement} img - Image element with .fade-in class
   */
  function processImageElement(img) {
    handleImageLoad(img, img);
  }

  /**
   * Process a fade-in background element
   * @param {HTMLElement} element - Element with background-image and .fade-in class
   */
  function processBackgroundElement(element) {
    const backgroundUrl = element.style.backgroundImage;
    if (!backgroundUrl) return;

    const img = new Image();
    img.src = backgroundUrl.replace(/url\(['"]?(.*?)['"]?\)/i, '$1');
    handleImageLoad(element, img);
  }

  /**
   * Process a single element that has .fade-in class
   * @param {HTMLElement} element - Element to process
   */
  function processFadeInElement(element) {
    // Skip if already processed or loaded
    if (element.classList.contains('loaded')) return;

    // Handle img elements
    if (element.tagName === 'IMG') {
      processImageElement(element);
    }
    // Handle elements with background images
    else if (element.style.backgroundImage) {
      processBackgroundElement(element);
    }
  }

  /**
   * Process all fade-in elements in a container
   * @param {HTMLElement} container - Container to search within
   */
  function processAllFadeIns(container) {
    const fadeInElements = container.querySelectorAll('.fade-in:not(.loaded)');
    fadeInElements.forEach(processFadeInElement);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      processAllFadeIns(document);
    });
  } else {
    // DOM already loaded
    processAllFadeIns(document);
  }

  // Watch for dynamically added fade-in elements
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      // Check added nodes
      mutation.addedNodes.forEach(function(node) {
        // Only process element nodes
        if (node.nodeType !== 1) return;

        // Check if the node itself has fade-in
        if (node.classList && node.classList.contains('fade-in')) {
          processFadeInElement(node);
        }

        // Check for fade-in elements within the added node
        if (node.querySelectorAll) {
          processAllFadeIns(node);
        }
      });
    });
  });

  // Start observing the document
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})();
